import { leaderboardService, type LeaderboardScope } from '@/services/leaderboardService';
import type { LeaderboardEntry } from '@/types/models';
import { clearRoot, el, mount } from './dom';
import { showMenu } from './MenuOverlay';

export async function showRecords(initialScope: LeaderboardScope = 'allTime'): Promise<void> {
  clearRoot();

  const container = el(
    'div',
    {
      class:
        'fixed inset-0 flex flex-col items-stretch px-4 pb-6 fade-in overflow-hidden safe-top'
    },
    []
  );

  const header = el('div', { class: 'flex items-center justify-between mb-4 mt-2' }, [
    backButton(() => showMenu()),
    el('h2', { class: 'font-display text-3xl text-white' }, ['🏆 Récords'])
  ]);
  container.append(header);

  const tabsRow = el('div', { class: 'flex gap-2 mb-4' }, []);
  const list = el('div', { class: 'flex-1 overflow-y-auto panel p-3 space-y-2' }, [
    el('div', { class: 'text-white/60 text-center py-12' }, ['Cargando...'])
  ]);
  container.append(tabsRow, list);
  mount(container);

  let scope: LeaderboardScope = initialScope;

  const renderTabs = () => {
    tabsRow.innerHTML = '';
    tabsRow.append(
      tabButton('Histórico', scope === 'allTime', () => switchScope('allTime')),
      tabButton('Semanal', scope === 'weekly', () => switchScope('weekly'))
    );
  };

  const fetchSafe = async (s: LeaderboardScope): Promise<LeaderboardEntry[]> => {
    try {
      return await leaderboardService.top(s, 100);
    } catch (err) {
      console.error('records fetch failed', err);
      return [];
    }
  };

  const switchScope = async (s: LeaderboardScope) => {
    scope = s;
    renderTabs();
    list.innerHTML = '';
    list.append(el('div', { class: 'text-white/60 text-center py-12' }, ['Cargando...']));
    const entries = await fetchSafe(scope);
    renderList(entries);
  };

  const renderList = (entries: LeaderboardEntry[]) => {
    list.innerHTML = '';
    if (entries.length === 0) {
      list.append(
        el('div', { class: 'text-white/50 text-center py-12' }, ['Sin récords todavía. ¡Sé el primero!'])
      );
      return;
    }
    entries.forEach((e, i) => {
      list.append(rowFor(e, i + 1));
    });
  };

  renderTabs();
  const entries = await fetchSafe(scope);
  renderList(entries);
}

function tabButton(label: string, active: boolean, onClick: () => void): HTMLElement {
  const cls = active
    ? 'flex-1 py-2 rounded-xl bg-accent text-bg font-display text-sm uppercase'
    : 'flex-1 py-2 rounded-xl bg-panel text-white/60 font-display text-sm uppercase border border-white/10';
  const b = el('button', { class: cls }, [label]);
  b.addEventListener('click', onClick);
  return b;
}

function backButton(onClick: () => void): HTMLElement {
  const b = el('button', { class: 'btn-ghost px-4 py-2 text-sm' }, ['← Volver']);
  b.addEventListener('click', onClick);
  return b;
}

function rowFor(e: LeaderboardEntry, rank: number): HTMLElement {
  const rankClass =
    rank === 1
      ? 'text-warn'
      : rank === 2
        ? 'text-white'
        : rank === 3
          ? 'text-accent'
          : 'text-white/50';

  return el('div', { class: 'flex items-center gap-3 px-3 py-2 rounded-xl bg-bg/40' }, [
    el('div', { class: `font-display text-xl w-8 text-right ${rankClass}` }, [String(rank)]),
    el('div', { class: 'flex-1 min-w-0' }, [
      el('div', { class: 'truncate text-white text-sm font-semibold' }, [e.displayName || 'Anónimo']),
      el('div', { class: 'text-white/40 text-[11px]' }, [
        `Nivel ${e.level} · ${e.durationSeconds}s`
      ])
    ]),
    el('div', { class: 'font-display text-accent text-lg' }, [e.score.toLocaleString('es-MX')])
  ]);
}
